import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { logOut } from '../auth/authSlice';
import { mapEmailTrackingResponse, notSentTrackingDetails } from '../../utils/emailTracking';

/**
 * Loads the email-delivery timeline for one invoice by its internal numeric id.
 * A 404 means the invoice has never had a tracked email (no tracking row) and
 * is treated as a valid "not sent yet" state, not an error.
 */
export const fetchEmailTracking = createAsyncThunk(
  'emailTracking/fetch',
  async (invoiceId, thunkAPI) => {
    try {
      const res = await axios.get(`/adminsendgridwebhook/admin-invoice-tracking/${invoiceId}`);
      // Handle both a bare EmailTrackingStatusVM and the shared
      // { statusCode, message, response } envelope other endpoints use.
      const vm = res?.data?.response || res?.data;
      return mapEmailTrackingResponse(vm);
    } catch (error) {
      if (error?.response?.status === 404) {
        return notSentTrackingDetails();
      }
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const initialState = {
  details: null,
  loading: false,
  error: null,
};

const emailTrackingSlice = createSlice({
  name: 'emailTracking',
  initialState,
  reducers: {
    clearEmailTracking: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.details = null;
      })
      .addCase(fetchEmailTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.details = action.payload;
      })
      .addCase(fetchEmailTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch email tracking';
      })
      // Reset when the admin logs out.
      .addCase(logOut, () => initialState);
  },
});

export const { clearEmailTracking } = emailTrackingSlice.actions;

export default emailTrackingSlice.reducer;
